import { MultipleQueriesQuery, SearchResponse } from '@algolia/client-search';
import algoliasearch from 'algoliasearch/lite';
import {
	EfroOostNewsarticle,
	Event,
	MapsFigure,
	Newsarticle,
	OpOostNewsarticle,
	Permit,
	Project,
	RegioExpresNewsarticle,
	RoadMaintenance,
	Subsidy,
	WooRequest
} from 'api/graphql-types';
import { getUnixTime } from 'date-fns';

type BaseSearchParams = {
	sort?: 'asc' | 'desc';
	page?: number;
	limit: number;
	locale?: string;
};

type ThemeFilter = {
	themesIds?: string[];
};

type DateRangeFilter = {
	startDate?: Date;
	endDate?: Date;
};

class AlgoliaError extends Error {
	constructor(error: Error) {
		super(error?.message, {
			cause: error
		});
	}
}

const algolia = algoliasearch(
	process.env.NEXT_PUBLIC_ALGOLIA_APP_ID as string,
	process.env.NEXT_PUBLIC_ALGOLIA_API_KEY as string
);

interface AlgoliaSearchResult<T> {
	results: T[];
	total: number;
	page: number;
}

interface SearchParams {
	indexName: string;
	query: string;
	page: number;
	limit: number;
	filters: {
		facetFilters?: string[];
		numericFilters?: (string | string[])[];
	};
}

const createSearchParams = (
	query: string,
	sort: 'asc' | 'desc',
	page: number,
	limit: number,
	baseIndexName: string,
	filters: {
		facetFilters?: Record<string, string[]>;
		additionalFilters?: string[];
		dateRange?: { startDate?: Date; endDate?: Date };
	}
): SearchParams => {
	const indexName = `${baseIndexName}_${sort}`;
	const algoliaPage = page - 1;

	const facetFilters = [
		...Object.entries(filters.facetFilters || {}).flatMap(([key, values]) => values.map((value) => `${key}:${value}`)),
		...(filters.additionalFilters || [])
	].filter(Boolean);

	const numericFilters = filters.dateRange
		? mapDateRangeFilter(filters.dateRange.startDate, filters.dateRange.endDate)
		: [];

	return {
		indexName,
		query,
		page: algoliaPage,
		limit,
		filters: {
			facetFilters: facetFilters.length > 0 ? facetFilters : [],
			numericFilters: numericFilters || []
		}
	};
};

export const searchWooDocuments = async (
	searchValue: string,
	filters: {
		categoryIds: string[];
	} & DateRangeFilter,
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<WooRequest>> => {
	try {
		const baseFacetFilters = {
			'category.documentId': filters.categoryIds
		};

		const searchParams = createSearchParams(searchValue, sort, page, limit, 'woo-request', {
			facetFilters: baseFacetFilters,
			additionalFilters: [`locale:${locale}`],
			dateRange: {
				startDate: filters.startDate,
				endDate: filters.endDate
			}
		});

		const { results } = await algolia.search([
			{
				indexName: searchParams.indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters,
					numericFilters: searchParams.filters.numericFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<WooRequest>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

const mapDateRangeFilter = (startDate?: Date, endDate?: Date): (string | string[])[] | undefined => {
	const startDateUnix = startDate ? getUnixTime(startDate.setHours(0, 0, 0)) : undefined;
	const endDateUnix = endDate ? getUnixTime(endDate.setHours(23, 59, 59)) : undefined;
	const endDateLimit = 999999999999;

	if (!startDateUnix && !endDateUnix) {
		return [];
	}

	if (startDateUnix && !endDateUnix) {
		return [[`startDateUnix:${startDateUnix} TO ${endDateLimit}`]];
	}

	if (endDateUnix && !startDateUnix) {
		return [[`startDateUnix:0 TO ${endDateUnix}`, `endDateUnix:0 TO ${endDateUnix}`]];
	}

	return [[`startDateUnix:${startDateUnix} TO ${endDateUnix}`, `endDateUnix:${startDateUnix} TO ${endDateUnix}`]];
};

export const searchPermits = async (
	searchValue: string,
	filters: {
		permitLawsIds: string[];
		permitProductTypesIds: string[];
		themesIds: string[];
	},
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<Permit>> => {
	try {
		const searchParams = createSearchParams(searchValue, sort, page, limit, 'permit', {
			facetFilters: {
				'permitLaws.documentId': filters.permitLawsIds,
				'permitProductTypes.documentId': filters.permitProductTypesIds,
				'themes.documentId': filters.themesIds
			},
			additionalFilters: ['seo.noIndex:false', `locale:${locale}`]
		});

		const { results } = await algolia.search([
			{
				indexName: searchParams.indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<Permit>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

export const searchSubsidy = async (
	searchValue: string,
	filters: {
		status: string;
		subsidyKindsIds: string[];
		subsidyScalesIds: string[];
		targetGroupsIds: string[];
		themesIds: string[];
	},
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<Subsidy>> => {
	try {
		const searchParams = createSearchParams(searchValue, sort, page, limit, 'subsidy', {
			facetFilters: {
				'subsidy_kinds.documentId': filters.subsidyKindsIds,
				'subsidy_scales.documentId': filters.subsidyScalesIds,
				'target_groups.documentId': filters.targetGroupsIds,
				'themes.documentId': filters.themesIds
			},
			additionalFilters: ['hideFromOverview:false', `subsidystatus:${filters.status}`, `locale:${locale}`]
		});

		const { results } = await algolia.search([
			{
				indexName: searchParams.indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<Subsidy>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

export const searchMapsFigures = async (
	searchValue: string,
	filters: {
		themesIds?: string[];
		type?: string;
	},
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<MapsFigure>> => {
	try {
		const searchParams = createSearchParams(searchValue, sort, page, limit, 'maps-figures', {
			facetFilters: {
				'themes.documentId': filters.themesIds || [],
				type: filters.type ? [filters.type] : []
			},
			additionalFilters: [`locale:${locale}`]
		});

		const { results } = await algolia.search([
			{
				indexName: searchParams.indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<MapsFigure>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

export const searchNewsArticles = async (
	searchValue: string,
	filters: ThemeFilter,
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams,
	subsite: 'regio-expres' | 'op-oost' | 'efro-oost' | 'gelderland' = 'gelderland'
): Promise<AlgoliaSearchResult<Newsarticle | RegioExpresNewsarticle | OpOostNewsarticle | EfroOostNewsarticle>> => {
	try {
		const prefix = subsite !== 'gelderland' ? `${subsite}-` : '';
		const indexName = `${prefix}newsarticle_${sort}`;

		const searchParams = createSearchParams(searchValue, sort, page, limit, indexName, {
			facetFilters: {
				'theme.documentId': filters.themesIds || []
			},
			additionalFilters: ['seo.noIndex:false', `locale:${locale}`]
		});

		const { results } = await algolia.search([
			{
				indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<
			Newsarticle | RegioExpresNewsarticle | OpOostNewsarticle | EfroOostNewsarticle
		>;

		return {
			total: result.nbHits,
			page: result.page + 1,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

export const searchEvents = async (
	searchValue: string,
	filters: ThemeFilter,
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<Event>> => {
	try {
		const searchParams = createSearchParams(searchValue, sort, page, limit, 'event', {
			facetFilters: {
				'theme.documentId': filters.themesIds || []
			},
			additionalFilters: [`locale:${locale}`]
		});

		const { results } = await algolia.search([
			{
				indexName: searchParams.indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<Event>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

export const searchProjects = async (
	searchValue: string,
	filters: ThemeFilter,
	{ sort = 'desc', page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<Project>> => {
	try {
		const searchParams = createSearchParams(searchValue, sort, page, limit, 'project', {
			facetFilters: {
				'theme.documentId': filters.themesIds || []
			},
			additionalFilters: ['seo.noIndex:false', `locale:${locale}`]
		});

		const { results } = await algolia.search([
			{
				indexName: searchParams.indexName,
				query: searchParams.query,
				params: {
					hitsPerPage: searchParams.limit,
					page: searchParams.page,
					facetFilters: searchParams.filters.facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<Project>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

export const searchRoadMaintenance = async (
	searchValue: string,
	{ page = 1, limit, locale = 'nl' }: BaseSearchParams
): Promise<AlgoliaSearchResult<RoadMaintenance>> => {
	try {
		const statusFilter = ['roadMaintenanceStatus:Active', 'roadMaintenanceStatus:Planned'];
		const algoliaPage = page - 1; // Starts at 0 instead of 1 in Algolia

		const facetFilters = [statusFilter, 'seo.noIndex:false', `locale:${locale}`];

		const { results } = await algolia.search([
			{
				indexName: 'road-maintenance',
				query: searchValue,
				params: {
					hitsPerPage: limit,
					page: algoliaPage,
					facetFilters
				}
			}
		]);

		const result = results[0] as SearchResponse<RoadMaintenance>;

		return {
			total: result.nbHits,
			page: result.page,
			results: result.hits
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};

interface WithPublishedDate {
	publishedAtUnix: number;
}

type GlobalSearchTypes =
	| (Newsarticle & WithPublishedDate)
	| (RegioExpresNewsarticle & WithPublishedDate)
	| (OpOostNewsarticle & WithPublishedDate)
	| (EfroOostNewsarticle & WithPublishedDate)
	| (Permit & WithPublishedDate)
	| (Subsidy & WithPublishedDate)
	| (Event & WithPublishedDate)
	| (Project & WithPublishedDate)
	| (RoadMaintenance & WithPublishedDate)
	| (MapsFigure & WithPublishedDate);

export const searchGlobal = async (
	searchValue: string,
	filters: DateRangeFilter,
	{ sort = 'desc', locale = 'nl' }: BaseSearchParams,
	subsite: 'regio-expres' | 'op-oost' | 'efro-oost' | 'gelderland' = 'gelderland'
): Promise<AlgoliaSearchResult<GlobalSearchTypes>> => {
	try {
		const endDateFilter = filters.endDate ? `publishedAtUnix <= ${getUnixTime(filters.endDate)}` : null;
		const startDateFilter = filters.startDate ? `publishedAtUnix >= ${getUnixTime(filters.startDate)}` : null;

		const numericFilters = [startDateFilter, endDateFilter].filter(Boolean);
		const facetFilters = ['seo.noIndex:false', `locale:${locale}`];

		let indexes = [
			{
				indexName: `${subsite === 'gelderland' ? '' : `${subsite}-`}page`,
				query: searchValue,
				params: { numericFilters, facetFilters }
			}
		] as MultipleQueriesQuery[];

		if (subsite === 'gelderland') {
			indexes.push(
				{
					indexName: `permit`,
					query: searchValue,
					params: { numericFilters, facetFilters }
				},
				{
					indexName: `subsidy`,
					query: searchValue,
					params: { numericFilters, facetFilters }
				},
				{
					indexName: `${subsite === 'gelderland' ? '' : `${subsite}-`}newsarticle`,
					query: searchValue,
					params: { numericFilters, facetFilters }
				},
				{
					indexName: `event`,
					query: searchValue,
					params: { numericFilters }
				},
				{
					indexName: `project`,
					query: searchValue,
					params: { numericFilters, facetFilters }
				},
				{
					indexName: `road-maintenance`,
					query: searchValue,
					params: { numericFilters, facetFilters }
				},
				{
					indexName: `maps-figures`,
					query: searchValue,
					params: { numericFilters }
				}
			);
		}

		const { results } = await algolia.search(indexes);

		const allHits = results.reduce((acc, result: any) => {
			return acc.concat(result.hits);
		}, []) as GlobalSearchTypes[];

		return {
			total: allHits.length,
			page: 1,
			results: allHits.sort((a, b) => {
				return sort === 'desc' ? b.publishedAtUnix - a.publishedAtUnix : a.publishedAtUnix - b.publishedAtUnix;
			})
		};
	} catch (error: any) {
		throw new AlgoliaError(error);
	}
};
